import { useCallback } from "react";
import { useBuilderStore } from "../_stores/builder-store";

/**
 * Returns a factory that creates onClick handlers for clickable canvas elements.
 * Each handler stops propagation, selects the parent component, and sets the focused field.
 *
 * @param componentId - The component ID that owns the clickable element
 * @returns A function that accepts a fieldId and returns a React mouse-event handler
 *
 * @example
 * ```tsx
 * const fieldClick = useCanvasFieldClick(component.id);
 * <h1 onClick={fieldClick("hero-title")} className="cursor-pointer">Title</h1>
 * ```
 */
export function useCanvasFieldClick(componentId: string) {
  const setSelectedId = useBuilderStore((s) => s.setSelectedId);
  const setFocusedField = useBuilderStore((s) => s.setFocusedField);
  const setInlineEditingField = useBuilderStore((s) => s.setInlineEditingField);

  return useCallback(
    (fieldId: string) => (e: React.MouseEvent) => {
      e.stopPropagation();
      setSelectedId(componentId);
      setFocusedField(fieldId);
      // Clear any active inline editing when clicking a non-inline field
      setInlineEditingField(null);
    },
    [componentId, setSelectedId, setFocusedField, setInlineEditingField]
  );
}
